import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  getInventory,
  listPoolShippings,
  sellPoolItem,
  type ApiClient,
  type IframePoolOrderItem,
  type IframePoolShipping,
} from '../../../modules/api';
import { useInfinitePagination } from '../../../modules/pagination';
import { notifyBuyback } from '../../../modules/notifications';
import { Backdrop } from '../../../modules/design-system';
import { DropsTab } from './tabs/DropsTab';
import { DeliveriesTab } from './tabs/DeliveriesTab';

const PER_PAGE = 30;
// Background poll for the active tab. Belt-and-suspenders next to the
// visibilitychange reload: keeps a long-lived, focused tab from drifting stale
// (e.g. a card sold in another tab) even without a tab switch. Only runs while
// the tab is visible, so idle background tabs never poll.
const REFETCH_INTERVAL_MS = 30_000;
import { MyPacksHeader, type MyPacksTabDef, type MyPacksTabId } from './MyPacksHeader';
import { MyPacksStatsRow } from './MyPacksStatsRow';
import { VaultHint } from './VaultHint';
import { withTabCounts } from './tabCounts';
import { MyPacksFooter } from './MyPacksFooter';
import { VaultCardPage } from './card/VaultCardPage';
import { ShipCardPage, ShipmentInfoPage } from '../ShipCard';
import { SLEEK_PAGE_MIN_HEIGHT } from '../shared';
import { useMyPacksRoute } from '../../../pages/MyPacks/myPacksRoute';
import { useShippingDisabled } from '../../../modules/shipping';
import { useSession } from '../../../modules/session';
import { useWallet } from '../../../modules/wallet';
import { useBackHandler } from '../../../modules/backStack';

// No Packs tab in sleek: packs open at purchase (`open_immediately`), so
// nothing waits sealed (partner decision 2026-10-07).
const TABS: MyPacksTabDef[] = [
  // UI label stays "Cards" per Figma; internal tab id keeps the
  // `drops` name (matches backend terminology / existing call sites).
  { id: 'drops', label: 'Cards' },
  { id: 'deliveries', label: 'Shipping' },
];

export interface MyPacksPageProps {
  client: ApiClient;
  onBack: () => void;
  /** Drop the noise-SVG overlay from the Vault page gradient. Sleek's
   *  sub-pages are flat already and have no overlay to drop. */
  hideBackgroundPattern?: boolean;
  /** A card handed in from an external entry (the reveal "Confirm Shipping"
   *  CTA) to ship straight away. Used once; later shipments start from a
   *  card's own page. */
  deliverySeed?: IframePoolOrderItem[];
  /** The Vault is the first screen of this load — see `MyPacksHeader`. */
  isEntryScreen?: boolean;
}

/**
 * Vault — two tabs over a flat dark page (partner Figma 2026-10-06 / 10-08).
 *
 *   Cards      — opened pool_items as a list (was "Drops" before the
 *                redesign); a row opens the card's page, which sells it or
 *                ships it on its own Ship Card page — one card at a time.
 *   Shipping   — the player's shipments; a row opens its details.
 *
 * Background gradient + every visible colour flows through Design
 * Constructor knobs (`--co-MyPacks*-*`); see `packages/theme-constructor/src/schema/components.ts`.
 */
export function MyPacksPage({
  client,
  onBack,
  hideBackgroundPattern = false,
  deliverySeed,
  isEntryScreen = false,
}: MyPacksPageProps) {
  const [tab, setTab] = useState<MyPacksTabId>('drops');
  // The card whose page is open, and the card on the Ship Card page — seeded
  // once by the reveal's "Confirm Shipping" CTA.
  const [openCard, setOpenCard] = useState<IframePoolOrderItem | null>(null);
  const [shipItem, setShipItem] = useState<IframePoolOrderItem | null>(deliverySeed?.[0] ?? null);
  const { route, push: navigate, replace: replaceRoute } = useMyPacksRoute();
  const session = useSession();
  const wallet = useWallet();

  const disableShipping = useShippingDisabled();

  // Log out is offered only to embed self-registered players — the ones who
  // signed up through the iframe (SessionContext.embedKey is set). Operator-
  // and partner-provided sessions launch with a session_token and no embedKey,
  // so their `onLogout` stays undefined and the control never renders.
  const handleLogout = useCallback(() => {
    // Leave My Packs for the home/pack page first (a reload would re-read the
    // my-packs URL and land back here), then drop the token → anonymous.
    onBack();
    session.dropToken();
  }, [onBack, session]);

  // Hide the Shipping tab entirely when shipping is disabled; also guard
  // against a stale `tab` value landing on the deliveries panel.
  const visibleTabs = disableShipping ? TABS.filter((t) => t.id !== 'deliveries') : TABS;
  const activeTab = disableShipping && tab === 'deliveries' ? 'drops' : tab;

  // The create-delivery route needs a card to ship: a deep link (or a reload)
  // without one — or with shipping disabled — falls back to the Vault.
  const hasShipItem = shipItem !== null;
  useEffect(() => {
    if (route.kind === 'delivery-new' && (disableShipping || !hasShipItem)) {
      // Replace, not push: browser back onto this dead entry would
      // otherwise bounce forward again and trap the player in the Vault.
      replaceRoute({ kind: 'index' });
    }
  }, [disableShipping, hasShipItem, route.kind, replaceRoute]);

  // Host back button (modules/backStack): a delivery sub-route steps back to
  // the index, same as its own Back button.
  useBackHandler(route.kind !== 'index' || openCard !== null, () => closeTopView(), 'subroute');

  // Two paginated feeds — Drops (opened items) and Deliveries
  // (pool_shippings). All live at the page level so the
  // lists survive tab switches; a tab revisit refetches in the background
  // (stale-while-revalidate) instead of flashing skeletons. Fetchers are
  // memoised so the hook's bootstrap effect doesn't loop on every parent
  // render.
  // Every inventory response carries the whole-vault FMV sum in its meta, so
  // the total stays in step with the grid it arrived with (no separate fetch).
  const [vaultTotal, setVaultTotal] = useState<number | null>(null);
  const inventoryFetcher = useCallback(
    (page: number, per: number) =>
      getInventory(client, { page, per, includeTotalValue: true }).then((res) => {
        setVaultTotal(res.totalFairMarketValue);
        return { items: res.items, totalCount: res.totalCount };
      }),
    [client],
  );
  const shippingsFetcher = useCallback(
    (page: number, per: number) =>
      listPoolShippings(client, { page, per }).then((res) => ({
        items: res.shippings,
        totalCount: res.meta.totalCount,
      })),
    [client],
  );

  const drops = useInfinitePagination<IframePoolOrderItem>(inventoryFetcher, {
    per: PER_PAGE,
  });
  const deliveries = useInfinitePagination<IframePoolShipping>(shippingsFetcher, {
    per: PER_PAGE,
  });
  const countedTabs = withTabCounts(visibleTabs, {
    cards: drops.totalCount,
    isLoading: drops.initialLoading,
  });

  // Reload the visited tab's feed on every tab switch (the mount run is
  // skipped — each hook bootstraps itself). Needed because the backend
  // settles mutations asynchronously: e.g. right after a shipping is created
  // the shipped cards may still be present in the inventory response, so the
  // list must be re-requested when the user comes back to the tab. The reload
  // happens behind the current list (refetch is stale-while-revalidate) and
  // behind an empty state too, because the tabs skeleton on `initialLoading`
  // rather than on `loading`.
  const dropsRefetch = drops.refetch;
  const deliveriesRefetch = deliveries.refetch;
  const refetchActiveTab = useCallback(() => {
    if (activeTab === 'drops') void dropsRefetch();
    else if (activeTab === 'deliveries') void deliveriesRefetch();
  }, [activeTab, dropsRefetch, deliveriesRefetch]);

  const skipMountRefetchRef = useRef(true);
  useEffect(() => {
    if (skipMountRefetchRef.current) {
      skipMountRefetchRef.current = false;
      return;
    }
    refetchActiveTab();
  }, [refetchActiveTab]);

  // Keep the active tab fresh so a card sold in another tab disappears here —
  // the backend rejects the double sale, but a leftover sold card is confusing
  // and invites the attempt. Two triggers, both silent (they reload behind
  // whatever the tab already shows, list or empty state), both scoped to the
  // active tab and to a visible page so idle background tabs never poll:
  //   • visibilitychange — reloads the instant the user returns to the tab.
  //   • 30s interval — catches a long-lived focused tab that never switches.
  useEffect(() => {
    const refetchIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refetchActiveTab();
    };
    document.addEventListener('visibilitychange', refetchIfVisible);
    const intervalId = setInterval(refetchIfVisible, REFETCH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', refetchIfVisible);
      clearInterval(intervalId);
    };
  }, [refetchActiveTab]);

  const refetchAfterSell = useCallback(() => {
    void drops.refetch();
  }, [drops]);

  // Per-item in-flight latch — blocks a fast double-tap on one card's Sell
  // CTA from firing two sellPoolItem POSTs (and two buyback toasts). Resolves
  // whether the card sold. A POST
  // is never coalesced by the client's GET dedup, so the guard lives here.
  const sellingItemIdsRef = useRef(new Set<IframePoolOrderItem['id']>());

  const handleItemSell = useCallback(
    async (item: IframePoolOrderItem) => {
      if (!item.poolOrderId) return false;
      if (sellingItemIdsRef.current.has(item.id)) return false;
      sellingItemIdsRef.current.add(item.id);
      // Snapshot the gross before selling — the backend nulls the offer out.
      const gross = item.bestActiveOffer?.grossPrice ?? 0;
      let isSold = false;
      try {
        await sellPoolItem(client, item.poolOrderId, item.id);
        notifyBuyback(gross);
        void wallet.refresh({ expectCredit: true });
        isSold = true;
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to sell card',
        );
      } finally {
        sellingItemIdsRef.current.delete(item.id);
      }
      // Refetch on failure too: a card that can no longer be sold (e.g. it
      // is committed to a delivery) must drop its stale buyback pill.
      refetchAfterSell();
      return isSold;
    },
    [client, refetchAfterSell, wallet],
  );

  // Steps back one level: Ship Card or a shipment → the Vault (or the card
  // page it came from), the card page → the list.
  const closeTopView = () => {
    if (route.kind !== 'index') {
      setShipItem(null);
      navigate({ kind: 'index' });
      return;
    }
    setOpenCard(null);
  };

  const openShipCard = (item: IframePoolOrderItem) => {
    setShipItem(item);
    navigate({ kind: 'delivery-new' });
  };

  const handleShippingCreated = () => {
    // Shipping created — back to the Vault on the Shipping tab. Refetch both
    // feeds: the hooks live at page level (no remount), and the shipped card
    // must leave the Cards list.
    setShipItem(null);
    setOpenCard(null);
    setTab('deliveries');
    void deliveries.refetch();
    refetchAfterSell();
    navigate({ kind: 'index' });
  };

  // Sub-views keep the Vault's flat page background behind them.
  const subView = (view: ReactNode) => (
    <div data-page="my-packs">
      <Backdrop bgImageUrl={null} />
      {view}
    </div>
  );

  if (route.kind === 'delivery-new' && !disableShipping && shipItem) {
    return subView(
      <ShipCardPage client={client} item={shipItem} onBack={closeTopView} onCreated={handleShippingCreated} />,
    );
  }

  if (route.kind === 'delivery-show') {
    return subView(<ShipmentInfoPage client={client} shippingId={route.id} onBack={closeTopView} />);
  }

  if (openCard && route.kind === 'index') {
    return subView(
      <VaultCardPage
        item={openCard}
        onBack={closeTopView}
        onSell={handleItemSell}
        onSold={() => setOpenCard(null)}
        {...(!disableShipping && { onShip: openShipCard })}
      />,
    );
  }

  return (
    <div
      data-page="my-packs"
      style={
        hideBackgroundPattern
          ? ({ '--co-MyPacksPageBackground-noiseOpacity': 0 } as CSSProperties)
          : undefined
      }
    >
      <Backdrop bgImageUrl={null} />
      {/* Optional photo layer above the grey gradient backdrop. When the
          `bgImage` knob is unset the CSS var resolves to `none` and this
          div contributes nothing; otherwise the `imageUrl` formatter
          ships a `url("…")` value rendered at cover-size, centred. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          backgroundImage: 'var(--co-MyPacksPageBackground-bgImage, none)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div
        data-co-component="MyPacksPageBackground"
        className={`@container relative flex ${SLEEK_PAGE_MIN_HEIGHT} w-full flex-col`}
      >
        <MyPacksHeader
          tabs={countedTabs}
          active={activeTab}
          onChange={(id) => {
            // Tab panels share the window scroll — reset it so each tab
            // opens at the top, same as a page navigation.
            window.scrollTo(0, 0);
            setTab(id);
          }}
          onHome={onBack}
          isEntryScreen={isEntryScreen}
          {...(session.embedKey && { onLogout: handleLogout })}
        />

        <div className="mx-auto flex w-full max-w-350 flex-1 flex-col">
        {activeTab === 'drops' && (
          <div
            data-co-component="MyPacksStatsBar"
            // Mobile: a flat edge-to-edge band (partner Figma 2026-10-06).
            // ≥600px: the same flat fill as a rounded plate in the Vault's
            // 640px column, aligned with the card list. `fill` overrides
            // both, `bandFill` only the band. Classes, not an inline
            // `background`, so the breakpoint can switch the shape.
            className="
              mb-3 flex flex-col px-5 py-3
              bg-[var(--co-MyPacksStatsBar-fill,var(--co-MyPacksStatsBar-bandFill,#1A1B1F))]
              @[600px]:mx-auto @[600px]:my-4 @[600px]:w-[calc(100%-3rem)] @[600px]:max-w-[640px]
              @[600px]:flex-row @[600px]:items-center @[600px]:justify-between @[600px]:gap-6 @[600px]:py-4
              @[600px]:[border-radius:var(--co-MyPacksStatsBar-borderRadius,18px)]
              @[600px]:[border:var(--co-MyPacksStatsBar-borderWidth,1px)_solid_var(--co-MyPacksStatsBar-borderColor,rgba(255,255,255,0.01))]
            "
          >
            <MyPacksStatsRow inventory={drops.items} total={vaultTotal} />
          </div>
        )}

        <main
          className={
            'flex-1 px-5 pb-12 @[600px]:px-6'
          }
        >
          {activeTab === 'drops' && <VaultHint />}
          {activeTab === 'drops' && (
            <DropsTab
              items={drops.items}
              initialLoading={drops.initialLoading}
              loadingMore={drops.loadingMore}
              reachedEnd={drops.reachedEnd}
              sentinelRef={drops.sentinelRef}
              onBrowse={onBack}
              onOpenItem={setOpenCard}
            />
          )}
          {activeTab === 'deliveries' && (
            <DeliveriesTab
              shippings={deliveries.items}
              initialLoading={deliveries.initialLoading}
              loadingMore={deliveries.loadingMore}
              reachedEnd={deliveries.reachedEnd}
              error={deliveries.error}
              onRetry={() => void deliveries.refetch()}
              sentinelRef={deliveries.sentinelRef}
              onOpenDelivery={(id) =>
                navigate({ kind: 'delivery-show', id })
              }
              onBrowse={onBack}
            />
          )}
        </main>

        <MyPacksFooter client={client} />
        </div>
      </div>
    </div>
  );
}
